import PreactImage from '@/components/snippets/image/image'
import { AccountSectionWrapper, ANCHOR_TAGS } from './AccountSectionWrapper'
import { PageModal } from './AccountModal'
import { FormAssignShoes } from './FormAssignShoe'
import { useToggle } from '../hooks/useToggle'
import { useAccountLayoutContext } from './AccountProfileContext'
import { useMemo, useState } from 'preact/hooks'
import { accountTranslations } from '../helpers/translations'
import LoaderSpin from '@/components/snippets/loader-spin/loader-spin'

export const AccountShoeWardrobeAlertBanner = ({ props }) => {
  return (
    <div className={'bg-secondary-paleblue bg-opacity-[0.24]'}>
      <div className={'flex flex-col justify-between gap-4 p-2 md:flex-row items-center'}>
        <div className={'flex flex-col'}>
          <p className={'paragraph-md font-bold'}>{props.title}</p>
          <p className={'paragraph-sm'}>{props.subTitle}</p>
        </div>
        <div
          data-role={props.cta.hook}
          onClick={props.cta.callback}
          className={'btn btn-md btn-solid-black justify-center w-fit'}
        >
          {props.cta.title}
        </div>
      </div>
    </div>
  )
}

const AccountShoeWardrobeCard = ({ props }) => {
  const { item, unassignItem } = props
  const size = item.product_variant.meta?.shopify?.option1 || null
  const [isLoading, setIsLoading] = useState(false)
  const image = useMemo(() => {
    return item.product_variant.images?.[0]?.url ?? item.product.images?.[0]?.url
  }, [item])

  const handleUnassign = async () => {
    setIsLoading(true)
    try {
      await unassignItem(item.id)
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={'flex flex-row gap-4'}>
      <div>
        <PreactImage
          imageClass="fit object-bottom h-full w-full object-cover"
          className={'aspect-1 h-32 w-32'}
          key={'id'}
          imageUrl={[image]}
        />
      </div>
      <div className={'flex flex-col justify-between gap-4'}>
        <div className={'flex flex-col justify-between'}>
          <p className={'paragraph-sm font-bold'}>{item.product.brand}</p>
          <p className={'paragraph-md'}>{item?.product_variant?.meta?.shopify?.customer_facing_name ?? item.product.title}</p>
          {!!size && (
            <div className={'text-neutrals-secondary-grey'}>
              <p className={'paragraph-sm text-neutrals-secondary-grey'}>Size: {size}</p>
            </div>
          )}
        </div>
        <button
          onClick={handleUnassign}
          className={'paragraph-sm text-left font-bold underline underline-offset-2 relative'}
          disabled={isLoading}
        >
          {isLoading && <LoaderSpin />}
          <span className={`${isLoading ? 'invisible' : ''}`}>Unassign</span>
        </button>
      </div>
    </div>
  )
}

/**
 * @param {{profile?: any, handleRefetch: () => void}} param0
 */
export const AccountShoeWardrobe = ({ profile, handleRefetch }) => {
  const { isActive, handleToggle } = useToggle(false)
  const { omneoClient } = useAccountLayoutContext()
  const { unassignedItems } = profile
  const pair = unassignedItems?.length > 1 ? 'pairs' : 'pair'
  const shoe = unassignedItems?.length > 1 ? 'shoes' : 'shoe'

  const unassignItem = async (itemId) => {
    try {
      const list = profile.assignedItems.id
      await omneoClient.removeWishlistItem(list, itemId)
      handleRefetch()
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <div className={'bg-white'}>
      <AccountSectionWrapper
        anchorTag={ANCHOR_TAGS.SHOE_WARDROBE.tag}
        title={accountTranslations.kids_profiles.shoe_wardrobe.title}
        showDivider={false}
      >
        <PageModal
          props={{
            modalTitle: accountTranslations.kids_profiles.shoe_wardrobe.cta,
            isActive,
            handleToggle,
            modal: (
              <FormAssignShoes
                profileChildren={profile?.children}
                handleToggle={handleToggle}
                handleRefetch={handleRefetch}
                shoes={unassignedItems}
              />
            )
          }}
        >
          <div className={'flex flex-col gap-4'}>
            {!!unassignedItems?.length && (
              <AccountShoeWardrobeAlertBanner
                props={{
                  title: `You have ${unassignedItems?.length} ${pair} of shoes to assign.`,
                  subTitle: 'If they were purchased in the last 30 days, you\'ll receive $2 rewards for each pair assigned.',
                  cta: {
                    title: `Assign ${shoe}`,
                    hook: 'shoe',
                    callback: handleToggle
                  }
                }}
              />
            )}
            <div className={'grid grid-cols-1 gap-6 md:grid-cols-2'}>
              {profile?.assignedItems?.items?.length ? (
                profile.assignedItems.items.map((item) => {
                  return (
                    <AccountShoeWardrobeCard
                      props={{
                        item,
                        unassignItem
                      }}
                    />
                  )
                })
              ) : (
                <p >{accountTranslations.kids_profiles.shoe_wardrobe.empty}</p>
              )}
            </div>
          </div>
        </PageModal>
      </AccountSectionWrapper>
    </div>
  )
}
